// Weighing-scale barcodes: label scales print an EAN-13 code that starts with
// an in-store prefix (20–29), then the item's code (PLU), then the weight or
// the price, then the check digit. Shops set the layout their scale uses.

export interface ScaleConfig {
  /** First digits that mark a scale barcode, e.g. ["21", "22"]. */
  prefixes: string[]
  /** Digits of the item code right after the prefix. */
  itemDigits: number
  /** Digits of the value, just before the check digit. */
  valueDigits: number
  /** What the value is: the weight (in the product's unit) or the price. */
  value: 'weight' | 'price'
  /** Decimals in the value: 3 for grams → kg (00450 = 0.450), 2 for paisa → rupees. */
  decimals: number
}

export const DEFAULT_SCALE_CONFIG: ScaleConfig = {
  prefixes: ['21'],
  itemDigits: 5,
  valueDigits: 5,
  value: 'weight',
  decimals: 3,
}

export type ScaleReading =
  | { plu: string; qtyMilli: number; amountPaisa?: undefined }
  | { plu: string; amountPaisa: number; qtyMilli?: undefined }

/** EAN-13 check digit of the first 12 digits. */
export function ean13CheckDigit(first12: string): number {
  let sum = 0
  for (let i = 0; i < 12; i++) sum += Number(first12[i]) * (i % 2 === 0 ? 1 : 3)
  return (10 - (sum % 10)) % 10
}

/** Why a layout can't work, or null when it can. */
export function scaleConfigProblem(config: ScaleConfig): string | null {
  if (config.prefixes.length === 0) return 'Enter at least one prefix'
  if (config.prefixes.some((p) => !/^\d{1,3}$/.test(p))) return 'Prefixes are 1–3 digits'
  if (!Number.isInteger(config.itemDigits) || config.itemDigits < 1)
    return 'Item digits must be 1 or more'
  if (!Number.isInteger(config.valueDigits) || config.valueDigits < 1)
    return 'Value digits must be 1 or more'
  if (!Number.isInteger(config.decimals) || config.decimals < 0 || config.decimals > 5)
    return 'Decimals must be 0–5'
  const longest = Math.max(...config.prefixes.map((p) => p.length))
  if (longest + config.itemDigits + config.valueDigits > 12)
    return 'Prefix, item and value digits must fit in 12 digits'
  return null
}

/** PLUs compare without leading zeros ("00123" = "123"). */
export function normalizePlu(plu: string): string {
  return plu.replace(/^0+(?=\d)/, '')
}

/**
 * Reads a scale barcode, or returns null when the code isn't one (other length
 * or prefix, wrong check digit, or a layout that doesn't fit).
 */
export function parseScaleBarcode(code: string, config: ScaleConfig): ScaleReading | null {
  const s = code.trim()
  if (!/^\d{13}$/.test(s) || scaleConfigProblem(config)) return null
  const prefix = config.prefixes.find((p) => s.startsWith(p))
  if (!prefix) return null
  if (ean13CheckDigit(s.slice(0, 12)) !== Number(s[12])) return null
  const plu = normalizePlu(s.slice(prefix.length, prefix.length + config.itemDigits))
  const value = Number(s.slice(12 - config.valueDigits, 12))
  if (config.value === 'weight') {
    const qtyMilli = Math.round((value * 1000) / 10 ** config.decimals)
    return qtyMilli > 0 ? { plu, qtyMilli } : null
  }
  const amountPaisa = Math.round((value * 100) / 10 ** config.decimals)
  return amountPaisa > 0 ? { plu, amountPaisa } : null
}
